// Surface navigation HUD: a compass strip, a coordinates readout, and a
// bearing/distance chip home. Instruments, not breadcrumbs — no minimap,
// no markers (vision anti-goals). Plain DOM like the rest of the UI.
// North is −z; bearings are degrees clockwise from north.
//
// Every word here is world-keyed. The chip is lit on any "surface" plan,
// and Meridian's plan is one: reading the frontier's biome field for a
// paved capital terrace made the instrument say "fen" over the Prom, and
// hung the frontier town's name on the capital's crown. The design refuses
// to degrade this readout precisely because it must not lie about physics
// — so it asks which world it is on instead.

import {
  type Carriage,
  carriageWord,
  groundWordAt,
  type PlayerPose,
  townChipNameFor,
  type WorldId,
} from "@bebop/shared";

const PX_PER_DEG = 2.2;
const CARDINALS: ReadonlyArray<{ deg: number; label: string }> = [
  { deg: 0, label: "N" },
  { deg: 45, label: "NE" },
  { deg: 90, label: "E" },
  { deg: 135, label: "SE" },
  { deg: 180, label: "S" },
  { deg: 225, label: "SW" },
  { deg: 270, label: "W" },
  { deg: 315, label: "NW" },
];

/** Heading in degrees [0, 360) from a camera orbit yaw. */
function headingDeg(orbitYaw: number): number {
  // Camera floor view direction is (−sin yaw, −cos yaw); north is −z.
  const deg = (-orbitYaw * 180) / Math.PI;
  return ((deg % 360) + 360) % 360;
}

function formatDistance(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
}

export class SurfaceHud {
  private root: HTMLDivElement;
  private track: HTMLDivElement;
  private coords: HTMLDivElement;
  private chip: HTMLDivElement;
  private chipArrow: HTMLSpanElement;
  private chipText: HTMLSpanElement;
  private carriage: HTMLDivElement;
  private ride: HTMLDivElement;
  private threat: HTMLDivElement;
  private threatTimer: number | undefined;

  constructor(parent: HTMLElement) {
    this.root = document.createElement("div");
    this.root.className = "surface-hud";
    this.root.innerHTML = `
      <div class="compass"><div class="compass-track"></div><div class="compass-needle"></div></div>
      <div class="settlement-chip"><span class="chip-arrow">▲</span><span class="chip-text"></span></div>
      <div class="ride-chip"></div>
      <div class="surface-coords"></div>
      <div class="carriage-line"></div>
      <div class="threat-line"></div>
    `;
    parent.append(this.root);

    this.track = this.root.querySelector(".compass-track") as HTMLDivElement;
    this.coords = this.root.querySelector(".surface-coords") as HTMLDivElement;
    this.chip = this.root.querySelector(".settlement-chip") as HTMLDivElement;
    this.chipArrow = this.root.querySelector(".chip-arrow") as HTMLSpanElement;
    this.chipText = this.root.querySelector(".chip-text") as HTMLSpanElement;
    this.carriage = this.root.querySelector(".carriage-line") as HTMLDivElement;
    this.ride = this.root.querySelector(".ride-chip") as HTMLDivElement;
    this.ride.style.visibility = "hidden";
    this.threat = this.root.querySelector(".threat-line") as HTMLDivElement;

    // Three wraps of marks so the strip can slide without gaps.
    for (let wrap = -1; wrap <= 1; wrap++) {
      for (const { deg, label } of CARDINALS) {
        const el = document.createElement("span");
        el.className = deg % 90 === 0 ? "compass-mark cardinal" : "compass-mark";
        el.textContent = label;
        el.dataset.deg = String(deg + wrap * 360);
        this.track.append(el);
      }
    }
    this.setVisible(false);
  }

  setVisible(visible: boolean): void {
    this.root.style.display = visible ? "" : "none";
  }

  /**
   * What the Edge is doing to you, in one word.
   *
   * This is the entire player-facing surface of the condition system: no
   * bar, no meter, no pips, no percentage, no countdown. You learn you are
   * in trouble the way a person does — by noticing you are cold, and then
   * that your hands have stopped working.
   */
  setCarriage(carriage: Carriage): void {
    const word = carriageWord(carriage);
    this.carriage.textContent = word ? `You are ${word}.` : "";
    this.carriage.dataset.carriage = carriage;
    this.carriage.style.visibility = word ? "visible" : "hidden";
  }

  /**
   * The ridden hull's state, in the settlement-chip idiom: two WORDS —
   * the wear band and the charge band — and nothing else. No bar, no
   * gauge, no pips, no percentage: a fill invites reading a number off a
   * thing that has none (trades.md §10, §13 — nothing degrades without
   * warning, and the warning is a word).
   */
  setRide(wearBand: string | undefined, chargeBand: string | undefined): void {
    if (!wearBand || !chargeBand) {
      this.ride.style.visibility = "hidden";
      return;
    }
    this.ride.style.visibility = "visible";
    const text = `running ${wearBand} · charge ${chargeBand}`;
    if (this.ride.textContent !== text) this.ride.textContent = text;
  }

  /**
   * The wilds speaking: one fading line of prose. The world warns in
   * words — this is the same telegraphy channel as the audio cue and the
   * creature's own posture, and like the carriage line it never carries
   * a number, a bearing readout, or a marker of any kind.
   */
  showThreat(text: string): void {
    this.threat.textContent = text;
    this.threat.classList.add("visible");
    if (this.threatTimer !== undefined) window.clearTimeout(this.threatTimer);
    this.threatTimer = window.setTimeout(() => {
      this.threat.classList.remove("visible");
    }, 6000);
  }

  /** Show a keypress hint ("E — board the skiff"), or hide it with null. */
  update(
    world: WorldId,
    pose: PlayerPose | undefined,
    orbitYaw: number,
    walking: boolean,
    speed?: number,
  ): void {
    if (!pose) return;
    const heading = headingDeg(orbitYaw);

    for (const el of this.track.children) {
      const mark = el as HTMLSpanElement;
      let rel = Number(mark.dataset.deg) - heading;
      if (rel > 540) rel -= 720;
      if (rel < -540) rel += 720;
      mark.style.transform = `translateX(${rel * PX_PER_DEG}px)`;
      mark.style.opacity = Math.abs(rel) > 80 ? "0" : "1";
    }

    const fx = pose.x >= 0 ? `+${Math.round(pose.x)}` : `${Math.round(pose.x)}`;
    const fz = pose.z >= 0 ? `+${Math.round(pose.z)}` : `${Math.round(pose.z)}`;
    const gait = speed !== undefined ? ` · ${speed.toFixed(1)} m/s` : walking ? " · walking" : "";
    this.coords.textContent = `${fx}, ${fz} · ${groundWordAt(world, pose.x, pose.z)}${gait}`;

    const dist = Math.hypot(pose.x, pose.z);
    if (dist < 220) {
      this.chip.style.visibility = "hidden";
    } else {
      this.chip.style.visibility = "visible";
      // Bearing of the vector pointing home, relative to the view.
      const bearing = (Math.atan2(-pose.x, pose.z) * 180) / Math.PI;
      const rel = ((bearing - heading) % 360) + 360;
      this.chipArrow.style.transform = `rotate(${rel}deg)`;
      this.chipText.textContent = `${townChipNameFor(world)} ${formatDistance(dist)}`;
    }
  }

  dispose(): void {
    this.root.remove();
  }
}
